import { useCallback, useEffect, useRef, useState } from "react"

import { parseAndThrowAppError } from "../../../../errors/errorProcessors"
import {
    deleteClientProtected,
    getClientProtected,
    postClientProtected,
} from "../../clients/clients"
import { dnoThresholdMapper } from "../../mappers/dnoThreshold/dnoThresholdMapper"
import { ILockStatusResponse } from "../../types/dnoThreshold/dnoThresholdTypes"
import { DNO_THRESHOLD_LOCK } from "../../urls/private"

const LOCK_DURATION_SECONDS = 600

interface IUseThresholdLock {
    lockStatus: ILockStatusResponse | null
    remainingSeconds: number
    isLockOwner: boolean
    acquireLock: () => Promise<boolean>
    releaseLock: () => Promise<void>
    refreshLockStatus: () => Promise<void>
    error: string | null
}

/**
 * Hook to manage the 10-minute threshold editing lock.
 * Handles acquiring, releasing, polling, and countdown.
 * @param username - Current user's username
 * @returns Lock state and control functions
 */
const useThresholdLock = (username: string): IUseThresholdLock => {
    const [lockStatus, setLockStatus] = useState<ILockStatusResponse | null>(
        null
    )
    const [remainingSeconds, setRemainingSeconds] = useState<number>(0)
    const [isLockOwner, setIsLockOwner] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)

    const clearTimers = useCallback((): void => {
        if (countdownRef.current) {
            clearInterval(countdownRef.current)
            countdownRef.current = null
        }
    }, [])

    const startCountdown = useCallback(
        (seconds: number): void => {
            clearTimers()
            if (seconds <= 0) return
            setRemainingSeconds(seconds)
            countdownRef.current = setInterval(() => {
                setRemainingSeconds((prev) => {
                    if (prev <= 1) {
                        clearTimers()
                        setIsLockOwner(false)
                        return 0
                    }
                    return prev - 1
                })
            }, 1000)
        },
        [clearTimers]
    )

    const refreshLockStatus = useCallback(async (): Promise<void> => {
        try {
            const res = await getClientProtected(DNO_THRESHOLD_LOCK)
            const status = dnoThresholdMapper.mapLockStatus(res)
            setLockStatus(status)
            const ownsLock = status.isLocked && status.lockedBy === username
            setIsLockOwner(ownsLock)

            if (ownsLock && status.remainingSeconds > 0) {
                startCountdown(status.remainingSeconds)
            }
        } catch (err) {
            const appError = parseAndThrowAppError(err)
            setError(appError.message)
        }
    }, [username, startCountdown])

    const acquireLock = useCallback(async (): Promise<boolean> => {
        try {
            setError(null)
            await postClientProtected(DNO_THRESHOLD_LOCK, {})
            setIsLockOwner(true)
            startCountdown(LOCK_DURATION_SECONDS)

            await refreshLockStatus()
            return true
        } catch (err) {
            await refreshLockStatus()
            return false
        }
    }, [startCountdown, refreshLockStatus])

    const releaseLock = useCallback(async (): Promise<void> => {
        try {
            setError(null)
            await deleteClientProtected(DNO_THRESHOLD_LOCK)
            clearTimers()
            setIsLockOwner(false)
            setRemainingSeconds(0)
            setLockStatus(null)
        } catch (err) {
            const appError = parseAndThrowAppError(err)
            setError(appError.message)
        }
    }, [clearTimers])

    // Fetch lock status once on mount
    useEffect(() => {
        refreshLockStatus()
    }, [refreshLockStatus])

    // Cleanup on unmount
    useEffect(
        () => (): void => {
            clearTimers()
        },
        [clearTimers]
    )

    return {
        lockStatus,
        remainingSeconds,
        isLockOwner,
        acquireLock,
        releaseLock,
        refreshLockStatus,
        error,
    }
}

export default useThresholdLock
